/**
 * Tiny dict-based i18n for the public client-share pages.
 *
 * No runtime dependency — just a typed map of {key: per-language string}
 * plus a ``t()`` helper that does {placeholder} substitution. Three locales
 * are supported: ``ka`` (Georgian, default), ``ru`` (Russian), ``en``
 * (English). The default lives on the share link record itself; the buyer
 * can override on the public page via a small flag picker in the top bar.
 *
 * Adding a key:
 *  1. Add the key to ``Strings`` so TypeScript flags every callsite that
 *     forgets to update.
 *  2. Add a value for each locale to ``DICT``. Russian and Georgian get
 *     hand-translated (no auto-translate); fall back to ``en`` only when
 *     the string is genuinely brand-style (e.g. "WhatsApp").
 */

import type { CSSProperties } from "react";

export type ShareLang = "ka" | "ru" | "en";

export const SUPPORTED_LANGS: ShareLang[] = ["ka", "ru", "en"];
export const DEFAULT_LANG: ShareLang = "ka";

export const LANG_LABELS: Record<ShareLang, { native: string; flag: string }> = {
  ka: { native: "ქართული", flag: "🇬🇪" },
  ru: { native: "Русский", flag: "🇷🇺" },
  en: { native: "English", flag: "🇬🇧" },
};

/** Coerce an unknown string (e.g. from the API or localStorage) into a
 *  supported ShareLang, falling back to the default. */
export function normalizeLang(value: string | null | undefined): ShareLang {
  if (value === "ka" || value === "ru" || value === "en") return value;
  return DEFAULT_LANG;
}

/**
 * Per-language font-role override for the Meridian brochure root, mirroring the
 * real meridianagency.ge ``layout.tsx`` locale swap. Cormorant Garamond / Roboto
 * / Spline Sans Mono cover latin + cyrillic (en, ru) but carry NO Georgian
 * glyphs, so ``ka`` points all three roles at the self-hosted BPG Glaho Sylfaen
 * face (mono keeps Spline as a fallback). For en/ru the ``.mshare`` defaults
 * already apply, so no inline override is needed (returns undefined).
 *
 * Spread onto the root ``.mshare`` element: ``style={meridianFontVars(lang)}``.
 */
export function meridianFontVars(
  lang: ShareLang,
): CSSProperties | undefined {
  if (lang !== "ka") return undefined;
  return {
    ["--font-display" as string]: '"BPG Glaho Sylfaen", Georgia, serif',
    ["--font-body" as string]: '"BPG Glaho Sylfaen", system-ui, sans-serif',
    ["--font-mono" as string]:
      '"BPG Glaho Sylfaen", "Spline Sans Mono", ui-monospace, monospace',
  } as CSSProperties;
}

// ─── strings ───────────────────────────────────────────────────────────────

const DICT = {
  // Top bar & agent card
  top_curated_by: {
    ka: "კურირებულია",
    ru: "Подобрано",
    en: "Curated by",
  },
  top_your_agent: {
    ka: "თქვენი აგენტი",
    ru: "вашим агентом",
    en: "your agent",
  },
  agent_card_label: {
    ka: "თქვენი აგენტი",
    ru: "Ваш агент",
    en: "Your agent",
  },
  agent_card_available: {
    ka: "მზად ვართ დაგეხმაროთ",
    ru: "Готов помочь",
    en: "Available now",
  },
  btn_call: { ka: "დარეკვა", ru: "Позвонить", en: "Call" },
  btn_call_agent: {
    ka: "აგენტთან დარეკვა",
    ru: "Позвонить агенту",
    en: "Call your agent",
  },
  btn_call_with_phone: {
    ka: "დარეკეთ {phone}",
    ru: "Позвонить {phone}",
    en: "Call {phone}",
  },
  cta_ready_title: {
    ka: "მოგწონთ რომელიმე ქონება?",
    ru: "Что-то понравилось?",
    en: "See something you like?",
  },
  cta_ready_blurb: {
    ka: "დაუკავშირდით აგენტს ჩვენების დაგეგმვისთვის ან თუ გაქვთ კითხვები.",
    ru: "Свяжитесь с агентом, чтобы организовать просмотр или задать вопрос.",
    en: "Reach out to your agent to schedule a viewing or ask anything.",
  },
  cta_ready_blurb_with_name: {
    ka: "დაუკავშირდით {name}-ს ჩვენების დაგეგმვისთვის ან თუ გაქვთ კითხვები.",
    ru: "Свяжитесь с {name}, чтобы организовать просмотр или задать вопрос.",
    en: "Reach out to {name} to schedule a viewing or ask any questions.",
  },
  btn_whatsapp: { ka: "WhatsApp", ru: "WhatsApp", en: "WhatsApp" },
  btn_email: { ka: "ელ. ფოსტა", ru: "Email", en: "Email" },
  btn_call_now: { ka: "დარეკეთ ახლავე", ru: "Позвонить", en: "Call now" },
  btn_schedule_whatsapp: {
    ka: "ჩაწერა WhatsApp-ით",
    ru: "Записаться через WhatsApp",
    en: "Schedule via WhatsApp",
  },

  // WhatsApp prefill messages
  whatsapp_default_msg: {
    ka: "გამარჯობა! მაინტერესებს ერთ-ერთი ქონება, რომელიც გამომიგზავნეთ.",
    ru: "Здравствуйте! Меня интересует одна из присланных квартир.",
    en: "Hi! I'm interested in one of the properties you sent me.",
  },
  whatsapp_listing_msg: {
    ka: "გამარჯობა! მაინტერესებს {title}. შეგვიძლია ჩვენების დაგეგმვა?",
    ru: "Здравствуйте! Меня интересует {title}. Можем договориться о просмотре?",
    en: "Hi! I'm interested in {title}. Could we schedule a viewing?",
  },
  email_subject: {
    ka: "შეკითხვა: {title}",
    ru: "Вопрос: {title}",
    en: "Question about {title}",
  },
  fallback_listing_label: {
    ka: "ეს ქონება",
    ru: "этот объект",
    en: "this property",
  },
  label_listing_generic: {
    ka: "ქონება",
    ru: "Объект",
    en: "Property",
  },

  // Intro block
  pill_handpicked: {
    ka: "შერჩეული ვარიანტები",
    ru: "Подборка для вас",
    en: "Hand-picked selection",
  },
  intro_headline_one: {
    ka: "ქონება სპეციალურად თქვენთვის",
    ru: "Объект, подобранный для вас",
    en: "A property picked just for you",
  },
  intro_headline_one_with_name: {
    // ``{name}`` substituted in the JSX layer so we can wrap the client
    // name in its own span if we want to style it later. Comma + name
    // reads naturally as a vocative in all three locales.
    ka: "ქონება სპეციალურად თქვენთვის, {name}",
    ru: "Объект, подобранный для вас, {name}",
    en: "A property picked just for you, {name}",
  },
  intro_headline_many_count_html: {
    // The {count} placeholder is wrapped in a span before substitution so the
    // number can be styled separately; the rest of the sentence stays one
    // unit per locale to preserve word-order.
    ka: "{count} ქონება სპეციალურად თქვენთვის",
    ru: "{count} объекта подобраны лично для вас",
    en: "{count} properties picked just for you",
  },
  intro_headline_many_with_name: {
    ka: "{count} ქონება სპეციალურად თქვენთვის, {name}",
    ru: "{count} объекта подобраны лично для вас, {name}",
    en: "{count} properties picked just for you, {name}",
  },
  // Georgian uses ergative case on the agent name — "თქვენმა აგენტმა X-მ/მა"
  // depending on whether X ends in a vowel. The JSX layer interpolates the
  // suffixed name between the two halves so the typography (bold name) stays
  // intact regardless of locale.
  intro_blurb_prefix: {
    ka: "თქვენმა აგენტმა ",
    ru: "Ваш агент ",
    en: "Your agent ",
  },
  intro_blurb_suffix_with_name: {
    ka: " შეგირჩიათ ვარიანტები ბაზრიდან, რომლებიც დიდი ალბათობით მოგეწონებათ. დააჭირეთ ნებისმიერ სურათს მთლიანი ინფორმაციის სანახავად — ფოტოები, განლაგება, უბანი და ჩვენების დაგეგმვა.",
    ru: " отобрал на рынке варианты, которые с наибольшей вероятностью вам подойдут. Нажмите любую карточку, чтобы увидеть всё — фото, планировку, район и как записаться на просмотр.",
    en: " narrowed the market to the listings most likely to fit. Tap any card to see the full story — photos, layout, neighborhood, and how to schedule a viewing.",
  },
  intro_blurb_no_name: {
    ka: "თქვენმა აგენტმა შეგირჩიათ ვარიანტები ბაზრიდან, რომლებიც დიდი ალბათობით მოგეწონებათ. დააჭირეთ ნებისმიერ სურათს მთლიანი ინფორმაციის სანახავად — ფოტოები, განლაგება, უბანი და ჩვენების დაგეგმვა.",
    ru: "Ваш агент отобрал на рынке варианты, которые с наибольшей вероятностью вам подойдут. Нажмите любую карточку, чтобы увидеть всё — фото, планировку, район и как записаться на просмотр.",
    en: "Your agent narrowed the market to the listings most likely to fit. Tap any card to see the full story — photos, layout, neighborhood, and how to schedule a viewing.",
  },

  // Hero card / nav
  badge_featured: { ka: "რეკომენდებული", ru: "Рекомендуем", en: "Featured" },
  counter_x_of_y: {
    ka: "{index} / {total}",
    ru: "{index} из {total}",
    en: "{index} of {total}",
  },
  btn_prev: { ka: "წინა", ru: "Назад", en: "Previous" },
  btn_next: { ka: "შემდეგი", ru: "Далее", en: "Next" },
  now_showing: { ka: "ნაჩვენებია", ru: "На экране", en: "Now showing" },
  view_full_details: {
    ka: "სრული ინფორმაცია",
    ru: "Подробнее",
    en: "View full details",
  },
  preview_arrow: { ka: "ნახვა ↑", ru: "Просмотр ↑", en: "Preview ↑" },
  view_arrow: { ka: "ნახვა →", ru: "Открыть →", en: "View →" },
  section_other_offers: {
    ka: "სხვა შერჩეული ქონება",
    ru: "Другие подобранные объекты",
    en: "Other properties picked for you",
  },
  section_other_offers_blurb: {
    ka: "კიდევ {count} ვარიანტი თქვენთვის — დააჭირეთ ნახვისთვის.",
    ru: "Ещё {count} вариантов в этой подборке — нажмите, чтобы открыть.",
    en: "{count} more options in this selection — tap any to open.",
  },

  // Sidebar / lower grid
  other_options: { ka: "სხვა ვარიანტები", ru: "Другие варианты", en: "Other options" },
  handpicked_count: {
    ka: "{count} შერჩეული",
    ru: "{count} в подборке",
    en: "{count} hand-picked",
  },
  all_properties: {
    ka: "ყველა შერჩეული ქონება",
    ru: "Все выбранные объекты",
    en: "All selected properties",
  },
  total_count: {
    ka: "სულ {count}",
    ru: "Всего {count}",
    en: "{count} total",
  },

  // Card / fact bits
  price_on_request: {
    ka: "ფასი მოთხოვნისამებრ",
    ru: "Цена по запросу",
    en: "Price on request",
  },
  abbrev_rooms: { ka: "ოთახ.", ru: "комн.", en: "rm" },
  rooms_with_count: {
    ka: "{count} ოთახი",
    ru: "{count} комн.",
    en: "{count} rooms",
  },
  no_photo: { ka: "ფოტო არ არის", ru: "Нет фото", en: "no photo" },
  per_sqm: {
    ka: "{symbol}{value} / მ²",
    ru: "{symbol}{value} / м²",
    en: "{symbol}{value} / m²",
  },

  // Fact labels (used on the detail page tiles)
  fact_rooms: { ka: "ოთახები", ru: "Комнаты", en: "Rooms" },
  fact_bedrooms: { ka: "საძინებელი", ru: "Спальни", en: "Bedrooms" },
  fact_bathrooms: { ka: "სველი წერტილი", ru: "Санузлы", en: "Bathrooms" },
  fact_area: { ka: "ფართი", ru: "Площадь", en: "Area" },
  fact_floor: { ka: "სართული", ru: "Этаж", en: "Floor" },
  fact_type: { ka: "ტიპი", ru: "Тип", en: "Type" },
  fact_building: { ka: "შენობა", ru: "Дом", en: "Building" },
  fact_condition: { ka: "მდგომარეობა", ru: "Состояние", en: "Condition" },
  fact_year_built: { ka: "ნაშენების წელი", ru: "Год постройки", en: "Year built" },
  fact_street: { ka: "ქუჩა", ru: "Улица", en: "Street" },
  fact_location: { ka: "ლოკაცია", ru: "Расположение", en: "Location" },

  // Detail page
  back_to_selection: { ka: "უკან არჩევანში", ru: "К списку", en: "Back to selection" },
  preparing_photos: {
    ka: "ფოტოები მუშავდება…",
    ru: "загружаются фото…",
    en: "preparing photos…",
  },
  no_photos_available: {
    ka: "ფოტოები ხელმისაწვდომი არ არის",
    ru: "нет доступных фото",
    en: "no photos available",
  },
  section_about: {
    ka: "ქონების შესახებ",
    ru: "Об объекте",
    en: "About this property",
  },
  section_features: {
    ka: "მახასიათებლები და ინფრასტრუქტურა",
    ru: "Особенности и инфраструктура",
    en: "Features & amenities",
  },
  aside_callout: {
    ka: "გაქვთ კითხვა ამ ქონების შესახებ, გსურთ ჩვენების დაგეგმვა ან სხვა ვარიანტებთან შედარება? თქვენი აგენტი ყველაზე სწრაფად დაგეხმარებათ.",
    ru: "Есть вопросы об объекте, хотите записаться на просмотр или сравнить с другими вариантами? Ваш агент быстрее всего поможет с этим.",
    en: "Have a question about this property, want to schedule a viewing, or need to compare it side-by-side with the others? Your agent is the fastest way to make it happen.",
  },

  // Closing CTA
  cta_headline: {
    ka: "მოგეწონათ? მოდით, ვისაუბროთ.",
    ru: "Понравилось? Давайте поговорим.",
    en: "Like what you see? Let's talk.",
  },
  cta_blurb: {
    ka: "დაგეგმეთ ჩვენება, დასვით კითხვები ან მიიღეთ დახმარება სხვა ვარიანტებთან შედარებაში.",
    ru: "Запишитесь на просмотр, задайте вопрос или сравните с другими вариантами.",
    en: "Schedule a viewing, ask questions, or get help comparing this with the other options your agent picked.",
  },

  // Loading / errors
  loading_title: { ka: "იტვირთება…", ru: "Загрузка…", en: "Loading…" },
  loading_grid_blurb: {
    ka: "ვიღებთ თქვენი აგენტის შერჩეულ ვარიანტებს…",
    ru: "Получаем подборку от вашего агента…",
    en: "Fetching the offers your agent picked for you…",
  },
  loading_detail_blurb: {
    ka: "ვიღებთ ქონების დეტალებს…",
    ru: "Получаем детали объекта…",
    en: "Fetching property details…",
  },
  err_link_unavailable: {
    ka: "ბმული მიუწვდომელია",
    ru: "Ссылка недоступна",
    en: "Link unavailable",
  },
  err_listing_unavailable: {
    ka: "ქონება მიუწვდომელია",
    ru: "Объект недоступен",
    en: "Listing unavailable",
  },
  err_listing_gone: {
    ka: "ეს ქონება აღარ არის ხელმისაწვდომი.",
    ru: "Этот объект больше недоступен.",
    en: "This listing isn't available anymore.",
  },
  err_share_missing: {
    ka: "ეს ბმული არ არსებობს ან წაშლილია.",
    ru: "Такой ссылки не существует или она удалена.",
    en: "This link doesn't exist or has been removed.",
  },
  err_share_gone: {
    ka: "ბმულის ვადა ამოიწურა ან აგენტმა გააუქმა.",
    ru: "Срок действия ссылки истёк или агент её отозвал.",
    en: "This link has expired or been revoked by your agent.",
  },
  err_failed_load: {
    ka: "ჩატვირთვა ვერ მოხერხდა",
    ru: "Не удалось загрузить",
    en: "Failed to load",
  },
  preparing_more: {
    ka: "მუშავდება კიდევ {count} — გვერდი თავისით განახლდება.",
    ru: "Готовим ещё {count} — страница обновится сама.",
    en: "Preparing {count} more — this page will refresh on its own.",
  },
  failed_no_offers: {
    ka: "ვერ მოვახერხეთ ვარიანტების მომზადება. გთხოვთ, აგენტს ახალი ბმული გამოაგზავნოს.",
    ru: "Не удалось подготовить варианты. Попросите агента прислать новую ссылку.",
    en: "We couldn't prepare the offers in time. Please ask your agent to send a fresh link.",
  },

  // Footer
  footer_curated: {
    ka: "შერჩეული თქვენთვის: {date} · ბმული მოქმედებს {expires}-მდე",
    ru: "Подборка от {date} · ссылка действует до {expires}",
    en: "Curated for you on {date} · link expires {expires}",
  },
  footer_tagline: {
    ka: "თბილისის ექსკლუზიური უძრავი ქონების ბუტიკ-სააგენტო.",
    ru: "Бутик-агентство эксклюзивной недвижимости в Тбилиси.",
    en: "A boutique agency for exclusive Tbilisi real estate.",
  },
  footer_selection_label: {
    ka: "შერჩევა",
    ru: "Подборка",
    en: "Selection",
  },
  footer_contact_label: {
    ka: "კონტაქტი",
    ru: "Контакты",
    en: "Contact",
  },

  // Language picker label
  language_picker_label: { ka: "ენა", ru: "Язык", en: "Language" },

  // Benchmark / deal-quality badge. Three positions match the broker
  // MatchCard chip vocabulary; the tooltips quantify how much under or
  // over the cohort median this listing is.
  bench_cheap: { ka: "შესანიშნავი ფასი", ru: "Отличная цена", en: "Great deal" },
  bench_average: { ka: "ბაზრის ფასი", ru: "Рыночная цена", en: "Fair price" },
  bench_expensive: { ka: "ბაზარზე ძვირი", ru: "Выше рынка", en: "Above market" },
  bench_pct_below: {
    ka: "საშუალოზე {percent}%-ით იაფი",
    ru: "на {percent}% ниже медианы",
    en: "{percent}% below the median",
  },
  bench_pct_above: {
    ka: "საშუალოზე {percent}%-ით ძვირი",
    ru: "на {percent}% выше медианы",
    en: "{percent}% above the median",
  },
  bench_at_median: {
    ka: "საშუალო ფასის მახლობლად",
    ru: "около медианы",
    en: "at the neighborhood median",
  },
  bench_tooltip_neighborhood: {
    ka: "შედარებულია უბნის საშუალო ფასთან: {value} $/მ²",
    ru: "Сравнение с медианой района: {value} $/м²",
    en: "Compared to the neighborhood median: {value} $/m²",
  },
  bench_tooltip_citywide: {
    ka: "შედარებულია ქალაქის საშუალო ფასთან: {value} $/მ²",
    ru: "Сравнение с медианой по городу: {value} $/м²",
    en: "Compared to the citywide median: {value} $/m²",
  },

  // Currency switcher
  currency_picker_label: { ka: "ვალუტა", ru: "Валюта", en: "Currency" },
  currency_native: { ka: "ორიგინალი", ru: "Оригинал", en: "Original" },
  currency_usd: { ka: "USD", ru: "USD", en: "USD" },

  // Description inline-edit (visible only to the broker who created
  // the link, not to the buyer / public viewer).
  edit_description: { ka: "რედაქტირება", ru: "Изменить", en: "Edit" },
  edit_save: { ka: "შენახვა", ru: "Сохранить", en: "Save" },
  edit_cancel: { ka: "გაუქმება", ru: "Отмена", en: "Cancel" },
  edit_saving: { ka: "ინახება…", ru: "Сохранение…", en: "Saving…" },
  edit_save_failed: {
    ka: "შენახვა ვერ მოხერხდა",
    ru: "Не удалось сохранить",
    en: "Save failed",
  },
  edit_placeholder: {
    ka: "ქონების აღწერა…",
    ru: "Описание объекта…",
    en: "Property description…",
  },
  edit_hint: {
    ka: "თქვენ ხედავთ რედაქტორს, რადგან თქვენ შექმენით ეს ბმული. ცვლილებები მაშინვე ჩანს მყიდველისთვის.",
    ru: "Вы видите редактор, потому что создали эту ссылку. Изменения сразу видны покупателю.",
    en: "You're seeing this editor because you created the link. Edits are visible to the buyer instantly.",
  },

  // Listing-details editor (owner only). Field labels reuse the ``fact_*``
  // keys above where they exist; the rest are defined here.
  edit_details: { ka: "დეტალების რედაქტირება", ru: "Изменить детали", en: "Edit details" },
  edit_details_title: { ka: "დეტალები", ru: "Детали", en: "Listing details" },
  field_title: { ka: "სათაური", ru: "Заголовок", en: "Title" },
  field_price: { ka: "ფასი", ru: "Цена", en: "Price" },
  field_currency: { ka: "ვალუტა", ru: "Валюта", en: "Currency" },
  field_deal_type: { ka: "გარიგების ტიპი", ru: "Тип сделки", en: "Deal type" },
  field_district: { ka: "რაიონი", ru: "Район", en: "District" },
  field_neighborhood: { ka: "უბანი", ru: "Микрорайон", en: "Neighborhood" },
  field_total_floors: { ka: "სართულები სულ", ru: "Этажность", en: "Total floors" },
  field_unset: { ka: "—", ru: "—", en: "—" },

  // Map / location section
  section_location: { ka: "ლოკაცია", ru: "Расположение", en: "Location" },
  open_in_google_maps: {
    ka: "Google Maps-ში გახსნა",
    ru: "Открыть в Google Maps",
    en: "Open in Google Maps",
  },
  no_coordinates: {
    ka: "ზუსტი კოორდინატები არ არის",
    ru: "Точные координаты отсутствуют",
    en: "Exact coordinates not available",
  },
} as const;

export type Strings = keyof typeof DICT;

/** Lookup + interpolate. Unknown placeholders are left intact (rare —
 *  TypeScript catches typos when keys come from constants). */
export function t(key: Strings, lang: ShareLang, params?: Record<string, string | number>): string {
  const entry = DICT[key];
  let raw = (entry?.[lang] ?? entry?.[DEFAULT_LANG] ?? key) as string;
  if (params) {
    for (const [name, value] of Object.entries(params)) {
      // ``split/join`` instead of ``replaceAll`` so we don't need
      // the lib.es2021 compiler option to compile this file.
      raw = raw.split(`{${name}}`).join(String(value));
    }
  }
  return raw;
}

// ─── domain label maps (per-locale) ────────────────────────────────────────

export const PROPERTY_TYPE_LABEL_BY_LANG: Record<ShareLang, Record<string, string>> = {
  ka: {
    apartment: "ბინა",
    house: "სახლი",
    cottage: "აგარაკი",
    land: "მიწის ნაკვეთი",
    commercial: "კომერციული",
  },
  ru: {
    apartment: "Квартира",
    house: "Дом",
    cottage: "Дача",
    land: "Земельный участок",
    commercial: "Коммерческая",
  },
  en: {
    apartment: "Apartment",
    house: "House",
    cottage: "Cottage",
    land: "Land",
    commercial: "Commercial",
  },
};

/**
 * Scraper rows store ``property_type`` / ``deal_type`` as GEORGIAN free-text
 * (e.g. "ბინა", "ქირავდება") rather than the canonical slugs the label maps
 * key on. These alias tables normalize the raw value (Georgian, English, or
 * an existing slug — placements/hot-offers already use slugs) to a canonical
 * key so the per-language labels below resolve. Unlike streets/districts, the
 * type/deal vocabulary is small and closed, so this fully translates.
 */
const PROPERTY_TYPE_ALIASES: Record<string, string> = {
  apartment: "apartment",
  house: "house",
  cottage: "cottage",
  land: "land",
  commercial: "commercial",
  "ბინა": "apartment",
  "სახლი": "house",
  "კერძო სახლი": "house",
  "აგარაკი": "cottage",
  "მიწის ნაკვეთი": "land",
  "მიწა": "land",
  "კომერციული ფართი": "commercial",
  "კომერციული": "commercial",
  "ოფისი": "commercial",
};
const DEAL_TYPE_ALIASES: Record<string, string> = {
  sale: "sale",
  rent: "rent",
  "იყიდება": "sale",
  "ქირავდება": "rent",
  "ქირავდება დღიურად": "rent",
  "დღიურად ქირავდება": "rent",
  "გირავდება": "rent",
};

/** Localized property-type label, tolerant of Georgian/slug/English input.
 *  Falls back to the raw value if the type is unrecognized. */
export function propertyTypeLabel(
  raw: string | null | undefined,
  lang: ShareLang,
): string | null {
  if (!raw) return null;
  const key = PROPERTY_TYPE_ALIASES[raw.trim()] ?? raw.trim();
  return PROPERTY_TYPE_LABEL_BY_LANG[lang][key] || raw;
}

/** Localized deal-type label ("For sale" / "For rent"), tolerant of
 *  Georgian/slug input. Falls back to the raw value. */
export function dealTypeLabel(
  raw: string | null | undefined,
  lang: ShareLang,
): string | null {
  if (!raw) return null;
  const key = DEAL_TYPE_ALIASES[raw.trim()] ?? raw.trim();
  return DEAL_TYPE_LABEL_BY_LANG[lang][key] || raw;
}

/**
 * A short, fully-translatable headline for a listing card / detail page.
 * The scraped ``title`` is free-text Georgian (untranslatable), so we lead
 * with the property type — which HAS per-language labels — and fall back to a
 * generic "Property" word when the type is unknown. Location (district /
 * neighborhood / street) stays in Georgian by product decision and is shown
 * separately, not folded into this headline.
 */
export function listingHeadline(
  item: { property_type?: string | null },
  lang: ShareLang,
): string {
  return propertyTypeLabel(item.property_type, lang) || t("label_listing_generic", lang);
}

export const DEAL_TYPE_LABEL_BY_LANG: Record<ShareLang, Record<string, string>> = {
  ka: {
    sale: "იყიდება",
    rent: "ქირავდება",
    daily_rent: "დღიური ქირა",
    mortgage: "გირაოთი",
  },
  ru: {
    sale: "Продажа",
    rent: "Аренда",
    daily_rent: "Посуточно",
    mortgage: "Ипотека",
  },
  en: {
    sale: "For sale",
    rent: "For rent",
    daily_rent: "Daily rent",
    mortgage: "Mortgage",
  },
};

export const BUILDING_STATUS_LABEL_BY_LANG: Record<ShareLang, Record<string, string>> = {
  ka: {
    new_built: "ახალი აშენებული",
    old_built: "ძველი აშენებული",
    under_construction: "მშენებარე",
  },
  ru: {
    new_built: "Новостройка",
    old_built: "Старый фонд",
    under_construction: "Строится",
  },
  en: {
    new_built: "Newly built",
    old_built: "Old construction",
    under_construction: "Under construction",
  },
};

export const CONDITION_LABEL_BY_LANG: Record<ShareLang, Record<string, string>> = {
  ka: {
    new_renovation: "ახალი გარემონტებული",
    old_renovation: "ძველი რემონტით",
    needs_renovation: "სარემონტო",
    green_frame: "მწვანე კარკასი",
    white_frame: "თეთრი კარკასი",
    black_frame: "შავი კარკასი",
  },
  ru: {
    new_renovation: "Новый ремонт",
    old_renovation: "Старый ремонт",
    needs_renovation: "Требует ремонта",
    green_frame: "Зелёный каркас",
    white_frame: "Белый каркас",
    black_frame: "Чёрный каркас",
  },
  en: {
    new_renovation: "Newly renovated",
    old_renovation: "Old renovation",
    needs_renovation: "Needs renovation",
    green_frame: "Green frame",
    white_frame: "White frame",
    black_frame: "Black frame",
  },
};

/** Locale code Intl.DateTimeFormat understands (BCP-47). */
export const INTL_LOCALE_BY_LANG: Record<ShareLang, string> = {
  ka: "ka-GE",
  ru: "ru-RU",
  en: "en-US",
};

/** Feature & amenity codes — canonical keys come from
 *  ``app/services/hot_offers.py`` (_SS_AMENITY_FLAGS + _SS_FEATURE_FLAGS).
 *  Both maps are merged at lookup time via {@link featureLabel} so callers
 *  don't need to know which bucket a code lives in. Add new portal codes
 *  here as the pipeline evolves; missing codes fall back to the raw key. */
export const FEATURE_AMENITY_LABEL_BY_LANG: Record<ShareLang, Record<string, string>> = {
  ka: {
    // Features
    balcony: "აივანი",
    loggia: "ლოჯია",
    basement: "სარდაფი",
    comfortable: "კომფორტული",
    garage: "ავტოფარეხი",
    pet_friendly: "ცხოველების მიღება დაშვებულია",
    urgent: "სასწრაფო",
    security_alarm: "სიგნალიზაცია",
    storage: "სათავსო",
    view_street: "ქუჩის ხედი",
    view_yard: "ეზოს ხედი",
    remote_viewing: "დისტანციური ჩვენება",
    has_3d: "3D ტური",
    light: "ნათელი",
    // Amenities
    air_conditioning: "კონდიციონერი",
    cable_tv: "საკაბელო ტელევიზია",
    drinking_water: "სასმელი წყალი",
    electricity: "ელექტროენერგია",
    elevator: "ლიფტი",
    fridge: "მაცივარი",
    furniture: "ავეჯი",
    glazed_windows: "მოშუშული ფანჯრები",
    heating: "გათბობა",
    hot_water: "ცხელი წყალი",
    internet: "ინტერნეტი",
    iron_door: "რკინის კარი",
    natural_gas: "ბუნებრივი აირი",
    sewage: "კანალიზაცია",
    telephone: "ტელეფონი",
    tv: "ტელევიზორი",
    washing_machine: "სარეცხი მანქანა",
    water: "წყალი",
    wifi: "Wi-Fi",
    built_in_kitchen: "ჩაშენებული სამზარეულო",
    pool: "აუზი",
  },
  ru: {
    balcony: "Балкон",
    loggia: "Лоджия",
    basement: "Подвал",
    comfortable: "Комфорт",
    garage: "Гараж",
    pet_friendly: "Можно с животными",
    urgent: "Срочно",
    security_alarm: "Сигнализация",
    storage: "Кладовая",
    view_street: "Вид на улицу",
    view_yard: "Вид во двор",
    remote_viewing: "Дистанционный просмотр",
    has_3d: "3D-тур",
    light: "Светлая",
    air_conditioning: "Кондиционер",
    cable_tv: "Кабельное ТВ",
    drinking_water: "Питьевая вода",
    electricity: "Электричество",
    elevator: "Лифт",
    fridge: "Холодильник",
    furniture: "Мебель",
    glazed_windows: "Остеклённые окна",
    heating: "Отопление",
    hot_water: "Горячая вода",
    internet: "Интернет",
    iron_door: "Железная дверь",
    natural_gas: "Природный газ",
    sewage: "Канализация",
    telephone: "Телефон",
    tv: "Телевизор",
    washing_machine: "Стиральная машина",
    water: "Вода",
    wifi: "Wi-Fi",
    built_in_kitchen: "Встроенная кухня",
    pool: "Бассейн",
  },
  en: {
    balcony: "Balcony",
    loggia: "Loggia",
    basement: "Basement",
    comfortable: "Comfortable",
    garage: "Garage",
    pet_friendly: "Pet-friendly",
    urgent: "Urgent",
    security_alarm: "Security alarm",
    storage: "Storage",
    view_street: "Street view",
    view_yard: "Yard view",
    remote_viewing: "Remote viewing",
    has_3d: "3D tour",
    light: "Bright",
    air_conditioning: "Air conditioning",
    cable_tv: "Cable TV",
    drinking_water: "Drinking water",
    electricity: "Electricity",
    elevator: "Elevator",
    fridge: "Fridge",
    furniture: "Furniture",
    glazed_windows: "Glazed windows",
    heating: "Heating",
    hot_water: "Hot water",
    internet: "Internet",
    iron_door: "Iron door",
    natural_gas: "Natural gas",
    sewage: "Sewage",
    telephone: "Telephone",
    tv: "TV",
    washing_machine: "Washing machine",
    water: "Water",
    wifi: "Wi-Fi",
    built_in_kitchen: "Built-in kitchen",
    pool: "Pool",
  },
};

/** Georgian ergative-case suffix for a personal name.
 *
 *  Rule (per native speakers + the user's own brief): names ending in
 *  a vowel — ა / ე / ი / ო / უ — take the short suffix ``-მ``; names
 *  ending in a consonant take ``-მა``. Latin-script names (e.g. broker
 *  names entered as "Keta", "Levan") follow the same rule on their last
 *  Latin vowel/consonant.
 *
 *  Example: ``ქეთა`` → ``ქეთამ``, ``ლევან`` → ``ლევანმა``,
 *           ``Keta`` → ``Ketaმ``,  ``Levan`` → ``Levanმა``.
 *
 *  This is only meaningful when rendering Georgian copy — Russian and
 *  English don't conjugate, so callers should only invoke this for
 *  ``lang === "ka"``.
 */
export function kaErgativeSuffix(name: string | null | undefined): string {
  if (!name) return "";
  const trimmed = name.trim();
  if (!trimmed) return "";
  const last = trimmed[trimmed.length - 1].toLowerCase();
  // Georgian + Latin vowels both rounded into one regex. Anything else
  // (Georgian or Latin consonant) falls into the ``-მა`` branch.
  return /[აეიოუaeiou]/.test(last) ? "მ" : "მა";
}

/** Lookup helper: returns a localised label for a feature/amenity code,
 *  or a "humanised" version of the raw code (snake_case → "Title Case")
 *  when the code is unknown. Tolerant of casing/whitespace variance. */
export function featureLabel(rawCode: string, lang: ShareLang): string {
  if (!rawCode) return rawCode;
  const code = rawCode.trim().toLowerCase().replace(/\s+/g, "_");
  const localised = FEATURE_AMENITY_LABEL_BY_LANG[lang][code];
  if (localised) return localised;
  // Soft fallback so an unknown code (e.g. a new portal flag) still
  // reads as a human label instead of "snake_case_eyesore".
  return code
    .split("_")
    .map((p) => (p ? p[0].toUpperCase() + p.slice(1) : p))
    .join(" ");
}
